import { css } from "@emotion/css";
import { Badge, Callout, SimpleDataTable } from "@octopusdeploy/design-system-components";
import { UnavailableIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { EphemeralEnvironmentTaskStatusResource, ProjectEphemeralEnvironmentDetail, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import type { MenuItem } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import DateFormatter from "~/utils/DateFormatter";
import { TaskStatusDetails } from "../Runbooks/NewRunbookRunsListPage/TaskStatusDetails";
import { EphemeralEnvironmentStatusIcon } from "./EphemeralEnvironmentStateIcon";
type ProjectEphemeralEnvironmentsTableProps = {
    project: ProjectResource;
    environments: ProjectEphemeralEnvironmentDetail[];
    deprovisionEnvironment: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    retryProvisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    retryDeprovisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markProvisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markDeprovisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
export function ProjectEphemeralEnvironmentsTable({ project, environments, deprovisionEnvironment, retryProvisioning, retryDeprovisioning, markProvisioningSuccessful, markDeprovisioningSuccessful }: ProjectEphemeralEnvironmentsTableProps) {
    return (<SimpleDataTable<ProjectEphemeralEnvironmentDetail> columns={[
            { title: "Environment", columnSize: "medium", render: (environment) => <EphemeralEnvironmentName environment={environment}/> },
            { title: "Status", columnSize: "small", render: (environment) => <EphemeralEnvironmentProjectStatusBadge environment={environment}/> },
            { title: "Latest Task", columnSize: "medium", render: (environment) => <EphemeralEnvironmentLatestTask environment={environment} project={project}/> },
            {
                title: "",
                columnSize: "x-small",
                render: (environment) => (<ProjectEphemeralEnvironmentOverflowMenu environment={environment} deprovisionEnvironment={deprovisionEnvironment} retryProvisioning={retryProvisioning} retryDeprovisioning={retryDeprovisioning} markProvisioningSuccessful={markProvisioningSuccessful} markDeprovisioningSuccessful={markDeprovisioningSuccessful}/>),
            },
        ]} data={environments} getRowKey={(environment) => environment.Id}/>);
}
interface EphemeralEnvironmentColumnProps {
    environment: ProjectEphemeralEnvironmentDetail;
}
function EphemeralEnvironmentName({ environment }: EphemeralEnvironmentColumnProps) {
    const style = {
        name: css({
            font: text.body.bold.medium,
        }),
    };
    return <div className={style.name}>{environment.Name}</div>;
}
function EphemeralEnvironmentProjectStatusBadge({ environment }: EphemeralEnvironmentColumnProps) {
    const icon = <EphemeralEnvironmentStatusIcon status={environment.Status} iconSize={20}/>;
    const badgeSize = "medium";
    switch (environment.Status) {
        case "NotProvisioned":
            return <Badge size={badgeSize} variant="neutral" label="Not Provisioned" icon={icon}/>;
        case "Provisioning":
            return <Badge size={badgeSize} variant="info" label="Provisioning" icon={icon}/>;
        case "ProvisioningFailed":
            return <Badge size={badgeSize} variant="danger" label="Provisioning failed" icon={icon}/>;
        case "Provisioned":
            return <Badge size={badgeSize} variant="success" label="Active" icon={icon}/>;
        case "Deprovisioning":
            return <Badge size={badgeSize} variant="info" label="Deprovisioning" icon={icon}/>;
        case "DeprovisioningFailed":
            return <Badge size={badgeSize} variant="danger" label="Deprovisioning failed" icon={icon}/>;
        case "Deprovisioned":
            return <Badge size={badgeSize} variant="neutral" label="Deprovisioned" icon={icon}/>;
        default:
            return <></>;
    }
}
interface EphemeralEnvironmentLatestTaskProps extends EphemeralEnvironmentColumnProps {
    project: ProjectResource;
}
function EphemeralEnvironmentLatestTask({ project, environment }: EphemeralEnvironmentLatestTaskProps) {
    if (!environment.LatestTask) {
        return <ProjectEphemeralEnvironmentNoTaskStatus />;
    }
    return <ProjectEphemeralEnvironmentTaskStatusDetails task={environment.LatestTask} project={project} environment={environment}/>;
}
interface ProjectEphemeralEnvironmentTaskStatusDetailsProps extends EphemeralEnvironmentLatestTaskProps {
    task: EphemeralEnvironmentTaskStatusResource;
}
function ProjectEphemeralEnvironmentTaskStatusDetails({ task, project, environment }: ProjectEphemeralEnvironmentTaskStatusDetailsProps) {
    const date = DateFormatter.dateToCustomFormat(task.StartTime, "MMMM Do YYYY h:mm A") ?? undefined;
    const ago = DateFormatter.momentAgo(task.StartTime);
    const spaceId = project.SpaceId;
    const projectSlug = project.Slug;
    let link = undefined;
    let title = undefined;
    if (task.TaskType === "RunbookRun") {
        title = task.RunbookRunTaskDetails.RunName;
        link = links.projectRunbookRunDetailPage.generateUrl({
            spaceId,
            projectSlug,
            runbookId: task.RunbookRunTaskDetails.RunbookId,
            runbookSnapshotId: task.RunbookRunTaskDetails.RunbookSnapshotId,
            runbookRunId: task.RunbookRunTaskDetails.RunbookRunId,
        });
    }
    else {
        title = `Run Deployment ${task.DeploymentTaskDetails.ReleaseVersion}`;
        link = links.deploymentDetailsPage.generateUrl({
            spaceId,
            projectSlug,
            releaseVersion: task.DeploymentTaskDetails.ReleaseVersion,
            deploymentId: task.DeploymentTaskDetails.DeploymentId,
        });
    }
    const styles = {
        taskStatus: css({
            display: "flex",
            alignItems: "center",
            overflow: "hidden", // Fallback/safety-net in case dates or anything else overflows here.
            minHeight: "3.25rem",
            borderRadius: borderRadius.small,
            "&:hover": {
                cursor: "pointer",
                outline: `1px solid ${themeTokens.color.border.selected}`,
                backgroundColor: themeTokens.color.background.primary.hovered,
            },
        }),
    };
    return (<div className={styles.taskStatus}>
            <TaskStatusDetails task={task} title={title} subtitle={`${ago}, ${date}`} link={link}/>
        </div>);
}
function ProjectEphemeralEnvironmentNoTaskStatus() {
    const noDataStyles = css({
        display: "flex",
        columnGap: space[6],
        font: text.body.bold.medium,
        color: themeTokens.color.text.disabled,
        alignItems: "center",
        padding: space[6],
    });
    return (<div className={noDataStyles}>
            <UnavailableIcon size={24}/>
            No Tasks
        </div>);
}
type ProjectEphemeralEnvironmentOverflowMenuProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    deprovisionEnvironment: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    retryProvisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    retryDeprovisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markProvisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
    markDeprovisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function ProjectEphemeralEnvironmentOverflowMenu({ environment, deprovisionEnvironment, retryProvisioning, retryDeprovisioning, markProvisioningSuccessful, markDeprovisioningSuccessful }: ProjectEphemeralEnvironmentOverflowMenuProps) {
    const overFlowActions: Array<MenuItem | MenuItem[]> = [];
    if (environment.Status === "Provisioned") {
        overFlowActions.push(OverflowMenuItems.destructiveItem("Deprovision", "Are you sure you want to deprovision this environment?", "Deprovision", "Deprovisioning", () => deprovisionEnvironment(environment), <DeprovisionEphemeralEnvironmentDialogContent environment={environment}/>));
    }
    else if (environment.Status === "ProvisioningFailed") {
        overFlowActions.push(OverflowMenuItems.dialogItem("Retry provisioning", <RetryProvisioningDialog environment={environment} retryProvisioning={retryProvisioning}/>), OverflowMenuItems.dialogItem("Mark as successful", <MarkProvisioningSuccessfulDialog environment={environment} markProvisioningSuccessful={markProvisioningSuccessful}/>));
    }
    else if (environment.Status === "DeprovisioningFailed") {
        overFlowActions.push(OverflowMenuItems.dialogItem("Retry deprovisioning", <RetryDeprovisioningDialog environment={environment} retryDeprovisioning={retryDeprovisioning}/>), OverflowMenuItems.dialogItem("Mark as successful", <MarkDeprovisioningSuccessfulDialog environment={environment} markDeprovisioningSuccessful={markDeprovisioningSuccessful}/>));
    }
    return <OverflowMenu menuItems={overFlowActions}/>;
}
type DeprovisionEphemeralEnvironmentDialogContentProps = {
    environment: ProjectEphemeralEnvironmentDetail;
};
function DeprovisionEphemeralEnvironmentDialogContent({ environment }: DeprovisionEphemeralEnvironmentDialogContentProps) {
    return (<Callout title="This is a destructive action" type={"danger"}>
            This action <strong>cannot</strong> be undone. This will deprovision the resources associated with environment <strong>{environment.Name}</strong> and delete it.
        </Callout>);
}
type RetryProvisioningDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    retryProvisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function RetryProvisioningDialog({ environment, retryProvisioning }: RetryProvisioningDialogProps) {
    return (<OkDialogLayout title="Retry provisioning" onOkClick={() => retryProvisioning(environment)} okButtonLabel="Retry" cancelButtonLabel="Cancel">
            <p>This will retry provisioning for the environment using the tip of the Git ref used for provisioning.</p>
        </OkDialogLayout>);
}
type RetryDeprovisioningDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    retryDeprovisioning: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function RetryDeprovisioningDialog({ environment, retryDeprovisioning }: RetryDeprovisioningDialogProps) {
    return (<OkDialogLayout title="Retry deprovisioning" onOkClick={() => retryDeprovisioning(environment)} okButtonLabel="Retry" cancelButtonLabel="Cancel">
            <p>This will retry deprovisioning for the environment using the tip of the Git ref used for deprovisioning.</p>
        </OkDialogLayout>);
}
type MarkProvisioningSuccessfulDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    markProvisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function MarkProvisioningSuccessfulDialog({ environment, markProvisioningSuccessful }: MarkProvisioningSuccessfulDialogProps) {
    return (<OkDialogLayout title="Mark provisioning as successful" onOkClick={() => markProvisioningSuccessful(environment)} okButtonLabel="Mark as successful" cancelButtonLabel="Cancel">
            <p>This will mark the provisioning of the environment as successful, ignoring the provisioning failure.</p>
            <Callout title="Warning" type="warning">
                Use this option only if you are confident the environment has been successfully provisioned, despite the provisioning runbook failure.
            </Callout>
        </OkDialogLayout>);
}
type MarkDeprovisioningSuccessfulDialogProps = {
    environment: ProjectEphemeralEnvironmentDetail;
    markDeprovisioningSuccessful: (environment: ProjectEphemeralEnvironmentDetail) => Promise<void>;
};
function MarkDeprovisioningSuccessfulDialog({ environment, markDeprovisioningSuccessful }: MarkDeprovisioningSuccessfulDialogProps) {
    return (<OkDialogLayout title="Mark deprovisioning as successful" onOkClick={() => markDeprovisioningSuccessful(environment)} okButtonLabel="Mark as successful" cancelButtonLabel="Cancel">
            <p>This will mark the deprovisioning of the environment as successful, ignoring the deprovisioning failure.</p>
            <Callout title="Warning" type="warning">
                Use this option only if you are confident any resources associated with the environment have been properly cleaned up, despite the deprovisioning runbook failing.
            </Callout>
        </OkDialogLayout>);
}
